/**
 * Group Lifecycle API Functions
 * Handles group creation, deletion, and renaming operations
 */

import { getAuthConfigFromStore, getAuthErrorMessage } from '../../auth/authUtils';
import { getApiGateway } from '../../api/endpointUtils';
import type {
  AddGroupParams,
  AddGroupResponse,
  GroupData,
  DeleteGroupParams,
  DeleteGroupResponse,
  RenameGroupParams,
  RenameGroupResponse,
  GroupRenameItem
} from '../types';

import { 
  GROUP_API_ERROR_MESSAGES,
  DELETE_GROUP_ERROR_MESSAGES,
  RENAME_GROUP_ERROR_MESSAGES
} from '../types';

/**
 * Add new groups to the fleet (Master privilege only)
 * Creates one or more groups with specified names
 * 
 * @param params - Group creation parameters (email and user_token from AuthStore if not provided)
 * @returns Promise with group creation response
 */
export async function addGroup(params: Omit<AddGroupParams, 'email' | 'user_token'> & {
  email?: string;
  user_token?: string;
}): Promise<AddGroupResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: AddGroupParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      ...params
    };

    // Validate required parameters
    if (!requestParams.groupNameList || requestParams.groupNameList.length === 0) {
      throw new Error('Group name list is required and cannot be empty');
    }
    
    // Validate group names
    requestParams.groupNameList.forEach((groupName, index) => {
      if (!groupName || groupName.trim().length === 0) {
        throw new Error(`Group name at index ${index} cannot be empty`);
      }
    });

    // Make API request
    const response = await fetch(`${API_GATEWAY}/Account/UserGroupAdd`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: AddGroupResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = GROUP_API_ERROR_MESSAGES[data.resultcode as keyof typeof GROUP_API_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}

/**
 * Add a single group to the fleet
 * Convenience function for adding a single group
 * 
 * @param groupName - Name of the group to create
 * @returns Promise with group creation response
 */
export async function addSingleGroup(groupName: string): Promise<GroupData> {
  const response = await addGroup({
    groupNameList: [groupName]
  });
  
  if (!response.response?.groupData || response.response.groupData.length === 0) {
    throw new Error('No group data returned from API');
  }
  
  return response.response.groupData[0];
}

/**
 * Add multiple groups to the fleet
 * Convenience function for adding multiple groups at once
 * 
 * @param groupNames - Array of group names to create
 * @returns Promise with group creation response
 */
export async function addMultipleGroups(groupNames: string[]): Promise<GroupData[]> {
  const response = await addGroup({
    groupNameList: groupNames
  });
  
  if (!response.response?.groupData) {
    throw new Error('No group data returned from API');
  }
  
  return response.response.groupData;
}

/**
 * Delete groups from the fleet (Master privilege only)
 * Removes one or more groups by their IDs
 * 
 * @param params - Group deletion parameters (email and user_token from AuthStore if not provided)
 * @returns Promise with group deletion response
 */
export async function deleteGroup(params: Omit<DeleteGroupParams, 'email' | 'user_token'> & {
  email?: string;
  user_token?: string;
}): Promise<DeleteGroupResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: DeleteGroupParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      ...params
    };

    // Validate required parameters
    if (!requestParams.groupDeleteIDList || requestParams.groupDeleteIDList.length === 0) {
      throw new Error('Group delete ID list is required and cannot be empty');
    }

    // Make API request
    const response = await fetch(`${API_GATEWAY}/Account/UserGroupDelete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: DeleteGroupResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = DELETE_GROUP_ERROR_MESSAGES[data.resultcode as keyof typeof DELETE_GROUP_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}

/**
 * Delete a single group from the fleet
 * Convenience function for deleting a single group
 * 
 * @param groupId - Group management ID to delete
 * @returns Promise with group deletion response
 */
export async function deleteSingleGroup(groupId: string): Promise<DeleteGroupResponse> {
  return deleteGroup({
    groupDeleteIDList: [groupId]
  });
}

/**
 * Delete multiple groups from the fleet
 * Convenience function for deleting multiple groups at once
 * 
 * @param groupIds - Array of group management IDs to delete
 * @returns Promise with group deletion response
 */
export async function deleteMultipleGroups(groupIds: string[]): Promise<DeleteGroupResponse> {
  return deleteGroup({
    groupDeleteIDList: groupIds
  });
}

/**
 * Rename groups in the fleet (Master privilege only)
 * Changes names of one or more groups
 * 
 * @param params - Group rename parameters (email and user_token from AuthStore if not provided)
 * @returns Promise with group rename response
 */
export async function renameGroup(params: Omit<RenameGroupParams, 'email' | 'user_token'> & {
  email?: string;
  user_token?: string;
}): Promise<RenameGroupResponse> {
  // Get auth config from store
  const authConfig = getAuthConfigFromStore();
  
  if (!authConfig) {
    throw new Error(getAuthErrorMessage());
  }

  try {
    // Get API Gateway URL from endpoint store
    const API_GATEWAY = getApiGateway();
    
    // Merge parameters with auth config
    const requestParams: RenameGroupParams = {
      email: authConfig.userEmail,
      user_token: authConfig.userToken,
      tokenType: 'web',
      ...params
    };

    // Validate required parameters
    if (!requestParams.groupRenameList || requestParams.groupRenameList.length === 0) {
      throw new Error('Group rename list is required and cannot be empty');
    }
    
    // Validate each rename item
    requestParams.groupRenameList.forEach((item, index) => {
      if (!item.groupID || item.groupID.trim().length === 0) {
        throw new Error(`Group ID at index ${index} cannot be empty`);
      }
      if (!item.groupName || item.groupName.trim().length === 0) {
        throw new Error(`Group name at index ${index} cannot be empty`);
      }
    });

    // Make API request
    const response = await fetch(`${API_GATEWAY}/Account/UserGroupRename`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authConfig.jwtToken}`,
      },
      body: JSON.stringify(requestParams)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data: RenameGroupResponse = await response.json();

    // Handle API error responses
    if (data.resultcode !== 'BC_ERR_OK') {
      const errorMessage = RENAME_GROUP_ERROR_MESSAGES[data.resultcode as keyof typeof RENAME_GROUP_ERROR_MESSAGES] 
        || data.message 
        || 'Unknown error occurred';
      throw new Error(errorMessage);
    }
    
    return data;

  } catch (error) {
    throw error;
  }
}

/**
 * Rename a single group in the fleet
 * Convenience function for renaming a single group
 * 
 * @param groupId - Group management ID to rename
 * @param newGroupName - New name for the group
 * @returns Promise with group rename response
 */
export async function renameSingleGroup(groupId: string, newGroupName: string): Promise<RenameGroupResponse> {
  return renameGroup({
    groupRenameList: [{
      groupID: groupId,
      groupName: newGroupName
    }]
  });
}

/**
 * Rename multiple groups in the fleet
 * Convenience function for renaming multiple groups at once
 * 
 * @param renameList - Array of group rename items
 * @returns Promise with group rename response
 */
export async function renameMultipleGroups(renameList: GroupRenameItem[]): Promise<RenameGroupResponse> {
  return renameGroup({
    groupRenameList: renameList
  });
}